import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAuth } from '../context/AuthContext';
import { projectAPI } from '../services/api';
import { StudentProjectListItem, RootStackParamList } from '../types';
import { mapProjectListItem } from '../utils/mappers';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Spacing, FontSize, BorderRadius } from '../theme/colors';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';

type Nav = NativeStackNavigationProp<RootStackParamList>;

function getStatusColor(status: string) {
  switch (status) {
    case 'Graded':
      return { bg: '#DCFCE7', text: '#16A34A' };
    case 'Submitted':
      return { bg: '#E0F2FE', text: '#0284C7' };
    case 'Late':
      return { bg: '#FEE2E2', text: '#DC2626' };
    default:
      return { bg: '#FEF3C7', text: '#D97706' };
  }
}

function getStatusLabel(status: string) {
  return status === 'NotSubmitted' ? 'Not Submitted' : status;
}

function formatDueDate(dateStr?: string) {
  if (!dateStr) return 'No due date';
  const due = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = (due.getTime() - today.getTime()) / (1000 * 3600 * 24);
  if (diff === 0) return 'Due Today';
  if (diff === 1) return 'Due Tomorrow';
  return `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

export function ProjectScreen({ isNested }: { isNested?: boolean }) {
  const navigation = useNavigation<Nav>();
  const { selectedStudent } = useAuth();
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [projects, setProjects] = useState<StudentProjectListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadProjects = useCallback(async () => {
    if (!selectedStudent) {
      setIsLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      const { data } = await projectAPI.getProjectsByClass(
        selectedStudent.classId,
        selectedStudent.enrollmentId
      );
      const mapped = (data?.data ?? []).map(mapProjectListItem);
      mapped.sort((a: StudentProjectListItem, b: StudentProjectListItem) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return dateB - dateA;
      });
      setProjects(mapped);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, [selectedStudent]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // The page keeps its header while it loads, with the standard loader inside it.
  if (isLoading) {
    return <WebPageShell title="Projects" maxWidth={1000}><LoadingSpinner message="Loading projects..." /></WebPageShell>;
  }

  const content = (
    <View style={[styles.container, isNested && { backgroundColor: 'transparent' }]}>
      <FlatList
        data={projects}
        scrollEnabled={!isNested}
        keyExtractor={item => item.id}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              loadProjects();
            }}
          />
        }
        renderItem={({ item }) => {
          const statusColors = getStatusColor(item.myStatus);
          return (
            <TouchableOpacity
              style={[styles.card, isMobile && styles.cardMobile]}
              onPress={() => navigation.navigate('ProjectDetail', { project: item })}>
              <View style={[styles.cardTop, isMobile && styles.cardTopMobile]}>
                <View style={styles.subjectBadge}>
                  <Text style={styles.subject}>{item.subjectName}</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: statusColors.bg }]}>
                  <Text style={[styles.statusText, { color: statusColors.text }]}>
                    {getStatusLabel(item.myStatus)}
                  </Text>
                </View>
              </View>
              <Text style={styles.hwTitle}>{item.title}</Text>

              <View style={[styles.cardFooter, isMobile && styles.cardFooterMobile]}>
                <FeatherIcon name="clock" size={14} color={colors.textSecondary} style={{ marginRight: 6 }} />
                <Text style={styles.dueDate}>{formatDueDate(item.dueDate)}</Text>
                {typeof item.totalMarks === 'number' && (
                  <Text style={styles.points}>
                    {'  •  '}
                    {item.myStatus === 'Graded' && typeof item.myReceivedMarks === 'number'
                      ? `${item.myReceivedMarks}/${item.totalMarks} pts`
                      : `${item.totalMarks} pts`}
                  </Text>
                )}
              </View>
            </TouchableOpacity>
          );
        }}
        ListEmptyComponent={
          <View style={styles.empty}>
            <FeatherIcon name="folder" size={48} color={colors.textMuted} style={{ marginBottom: 12 }} />
            <Text style={styles.emptyText}>No projects yet</Text>
          </View>
        }
        contentContainerStyle={styles.list}
      />
    </View>
  );

  if (isNested) return content;

  return <WebPageShell title="Projects" maxWidth={1000}>{content}</WebPageShell>;
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  list: { padding: Spacing.md },
  card: {
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: Spacing.lg,
    marginBottom: Spacing.md,
    elevation: 2,
    shadowColor: colors.primary,
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
  cardMobile: {
    padding: Spacing.md,
  },
  cardTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: Spacing.sm,
  },
  cardTopMobile: { flexWrap: 'wrap', gap: 8 } as any,
  subjectBadge: {
    backgroundColor: colors.primaryLight,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: BorderRadius.full,
  },
  subject: {
    fontSize: FontSize.xs,
    fontWeight: '700',
    color: colors.primary,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: BorderRadius.full,
  },
  statusText: { fontSize: FontSize.xs, fontWeight: '700' },
  hwTitle: {
    fontSize: FontSize.md,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 8,
    marginTop: 4,
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  cardFooterMobile: { flexWrap: 'wrap' } as any,
  dueDate: {
    fontSize: FontSize.sm,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  points: {
    fontSize: FontSize.sm,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  empty: { alignItems: 'center', paddingVertical: 60 },
  emptyText: { fontSize: FontSize.md, color: colors.textSecondary },
});
